/**
 * 2.1 bloque C (IA): contratos y lógica pura de búsqueda inteligente, artículos parecidos, precio
 * sugerido, respuestas sugeridas, revisión automática y cuentas duplicadas. Aquí solo lo que se puede
 * probar sin red ni Firestore: el texto que se convierte en vector, la mezcla de resultados, el
 * rango de precio, la edición «material» y la detección de cuentas que comparten un dato.
 */
import { z } from 'zod';
import { CountryCodeSchema, LanguageCodeSchema } from '../firestore/primitives.js';

// ---------- texto → vector ----------

/** Dimensión de los vectores (`text-multilingual-embedding-002`, Vertex AI). */
export const EMBEDDING_DIM = 768;
/** Colección server-only con un vector por anuncio (id = id del anuncio). `products` no cambia. */
export const PRODUCT_EMBEDDINGS = 'productEmbeddings';
/** Estados con vector: activos (búsqueda, parecidos) y vendidos (precio sugerido). */
export const EMBEDDED_STATUSES = ['active', 'sold'] as const;

export interface EmbeddableListing {
  title: string;
  description?: string | null;
  brand?: string | null;
  category_id?: string | null;
  size?: string | null;
  color?: string | null;
  material?: string | null;
}

const clean = (s: string | null | undefined): string => (s ?? '').replace(/\s+/g, ' ').trim();

/**
 * Texto que se convierte en vector: título y marca primero (lo que más pesa), categoría y atributos,
 * y la descripción recortada (más texto = más coste y no mejora la búsqueda).
 */
export function embeddingText(l: EmbeddableListing, maxDescription = 600): string {
  const parts = [
    clean(l.title),
    clean(l.brand),
    clean(l.category_id).replace(/_/g, ' '),
    [clean(l.size), clean(l.color), clean(l.material)].filter(Boolean).join(' '),
    clean(l.description).slice(0, maxDescription),
  ];
  return parts.filter(Boolean).join('\n');
}

// ---------- búsqueda: mezcla palabras + significado ----------

export interface SemanticHit {
  id: string;
  /** Distancia coseno (0 = idéntico, 2 = opuesto). */
  distance: number;
}

/** Por encima de esta distancia un resultado «por significado» ya no se parece a la consulta. */
export const SEMANTIC_MAX_DISTANCE = 0.55;

/**
 * Reciprocal Rank Fusion: lo que sale en las dos listas sube; lo que solo sale por palabras conserva
 * su orden; lo que solo sale por significado entra intercalado según su parecido. `k` = 60 (clásico).
 */
export function fuseResults(
  keywordIds: readonly string[],
  semantic: readonly SemanticHit[],
  opts: { k?: number; maxDistance?: number } = {},
): string[] {
  const k = opts.k ?? 60;
  const maxDistance = opts.maxDistance ?? SEMANTIC_MAX_DISTANCE;
  const score = new Map<string, number>();
  const firstSeen = new Map<string, number>();
  let order = 0;
  const add = (id: string, rank: number): void => {
    score.set(id, (score.get(id) ?? 0) + 1 / (k + rank + 1));
    if (!firstSeen.has(id)) firstSeen.set(id, order++);
  };
  keywordIds.forEach((id, i) => {
    add(id, i);
  });
  [...semantic]
    .filter((h) => h.distance <= maxDistance)
    .sort((a, b) => a.distance - b.distance)
    .forEach((h, i) => {
      add(h.id, i);
    });
  return [...score.keys()].sort(
    (a, b) =>
      (score.get(b) ?? 0) - (score.get(a) ?? 0) ||
      (firstSeen.get(a) ?? 0) - (firstSeen.get(b) ?? 0),
  );
}

// ---------- artículos parecidos ----------

export interface SimilarCandidate {
  id: string;
  distance: number;
  seller_id: string;
  category_id: string;
  category_root: string | null;
  title: string;
}

/** Por debajo de esta distancia y del mismo vendedor, es el mismo artículo publicado otra vez. */
export const SAME_ITEM_DISTANCE = 0.04;

const normTitle = (s: string): string =>
  s
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();

/**
 * Ordena candidatos para «También te puede interesar»: misma categoría primero (y misma raíz después),
 * sin el propio anuncio ni el mismo artículo repetido por el mismo vendedor; como mucho 3 del mismo
 * vendedor para que el bloque no sea su escaparate.
 */
export function rankSimilar(
  source: Pick<SimilarCandidate, 'id' | 'seller_id' | 'category_id' | 'category_root' | 'title'>,
  candidates: readonly SimilarCandidate[],
  max = 12,
): string[] {
  const srcTitle = normTitle(source.title);
  const bySeller = new Map<string, number>();
  const scored = candidates
    .filter((c) => c.id !== source.id)
    .filter(
      (c) =>
        !(
          c.seller_id === source.seller_id &&
          (c.distance <= SAME_ITEM_DISTANCE || normTitle(c.title) === srcTitle)
        ),
    )
    .map((c) => {
      const boost =
        c.category_id === source.category_id
          ? 0.15
          : source.category_root !== null && c.category_root === source.category_root
            ? 0.07
            : 0;
      return { c, s: c.distance - boost };
    })
    .sort((a, b) => a.s - b.s);
  const out: string[] = [];
  for (const { c } of scored) {
    const n = bySeller.get(c.seller_id) ?? 0;
    if (n >= 3) continue;
    bySeller.set(c.seller_id, n + 1);
    out.push(c.id);
    if (out.length >= max) break;
  }
  return out;
}

// ---------- precio sugerido ----------

export interface PriceComparable {
  /** Precio ya convertido a la moneda del vendedor. */
  price: number;
  distance: number;
  condition?: string | null;
}

export interface PriceRange {
  min: number;
  max: number;
  median: number;
  count: number;
}

/** Comparables mínimos para sugerir algo (con menos, mejor no decir nada). */
export const MIN_PRICE_COMPARABLES = 4;
export const PRICE_MAX_DISTANCE = 0.35;

function weightedQuantile(items: readonly { v: number; w: number }[], q: number): number {
  const sorted = [...items].sort((a, b) => a.v - b.v);
  const total = sorted.reduce((s, x) => s + x.w, 0);
  let acc = 0;
  for (const x of sorted) {
    acc += x.w;
    if (acc >= total * q) return x.v;
  }
  return sorted[sorted.length - 1]?.v ?? 0;
}

/** Redondeo «de tienda»: < 10 a 0,5; < 100 a 1; < 1000 a 5; si no a 10 (en unidades de la moneda). */
export function roundPrice(v: number, zeroDecimals = false): number {
  const step = v < 10 ? (zeroDecimals ? 1 : 0.5) : v < 100 ? 1 : v < 1000 ? 5 : 10;
  return Math.max(step, Math.round(v / step) * step);
}

/**
 * Rango P25–P75 ponderado por parecido (los más cercanos pesan más; el mismo estado, el doble). Sin
 * suficientes comparables cercanos devuelve null. Quita extremos absurdos (> 4× o < ¼ de la mediana).
 * `zeroDecimals`: monedas sin decimales (HUF, ISK) o de importes grandes, redondeo a enteros.
 */
export function suggestPriceRange(
  comps: readonly PriceComparable[],
  opts: {
    condition?: string | null;
    maxDistance?: number;
    minCount?: number;
    zeroDecimals?: boolean;
  } = {},
): PriceRange | null {
  const maxDistance = opts.maxDistance ?? PRICE_MAX_DISTANCE;
  const minCount = opts.minCount ?? MIN_PRICE_COMPARABLES;
  const near = comps.filter(
    (c) => c.price > 0 && Number.isFinite(c.price) && c.distance <= maxDistance,
  );
  if (near.length < minCount) return null;
  const weighted = near.map((c) => ({
    v: c.price,
    w:
      (1 - c.distance / (maxDistance * 1.25)) *
      (opts.condition && c.condition === opts.condition ? 2 : 1),
  }));
  const med = weightedQuantile(weighted, 0.5);
  const kept = weighted.filter((x) => x.v <= med * 4 && x.v >= med / 4);
  if (kept.length < minCount) return null;
  const z = opts.zeroDecimals === true;
  const lo = roundPrice(weightedQuantile(kept, 0.25), z);
  const hi = roundPrice(weightedQuantile(kept, 0.75), z);
  const median = roundPrice(weightedQuantile(kept, 0.5), z);
  return { min: Math.min(lo, median), max: Math.max(hi, median), median, count: kept.length };
}

/** Pasa un precio entre monedas con tipos «unidades por 1 EUR»; null si falta un tipo. */
export function convertPrice(
  amount: number,
  from: string,
  to: string,
  ratesPerEur: Readonly<Partial<Record<string, number>>>,
): number | null {
  if (from === to) return amount;
  const rf = from === 'EUR' ? 1 : ratesPerEur[from];
  const rt = to === 'EUR' ? 1 : ratesPerEur[to];
  if (!rf || !rt) return null;
  return (amount / rf) * rt;
}

// ---------- revisión automática: ¿la edición cambia lo que se revisó? ----------

export interface ModerationSnapshot {
  title: string;
  description: string;
  brand?: string | null;
  category_id: string;
  photos?: readonly string[];
  /** Precio del anuncio (unidades) y moneda: una bajada fuerte es señal de estafa/falsificación. */
  price?: number | null;
  currency?: string | null;
}

/** Cambio de precio que obliga a revisar de nuevo: otra moneda o ±30 % o más. */
export const MATERIAL_PRICE_CHANGE = 0.3;

/**
 * Título, descripción, marca, categoría o fotos distintos (espacios y mayúsculas no cuentan), u otro
 * precio de ±30 % o más / otra moneda (revisión del 4/11: publicar a 2.000 € y bajar a 20 € después).
 */
export function isMaterialEdit(
  before: ModerationSnapshot | null,
  after: ModerationSnapshot,
): boolean {
  if (!before) return true;
  const n = (s: string | null | undefined): string => clean(s).toLowerCase();
  if (n(before.title) !== n(after.title)) return true;
  if (n(before.description) !== n(after.description)) return true;
  if (n(before.brand) !== n(after.brand)) return true;
  if (before.category_id !== after.category_id) return true;
  if (typeof before.price === 'number' && typeof after.price === 'number') {
    if ((before.currency ?? '') !== (after.currency ?? '')) return true;
    const base = Math.max(Math.abs(before.price), 1e-9);
    if (Math.abs(after.price - before.price) / base >= MATERIAL_PRICE_CHANGE) return true;
  }
  const a = new Set(before.photos ?? []);
  const b = after.photos ?? [];
  return b.length !== a.size || b.some((p) => !a.has(p));
}

// ---------- cuentas duplicadas ----------

export const DUPLICATE_SIGNAL_KINDS = ['device', 'card', 'iban', 'phone'] as const;
export type DuplicateSignalKind = (typeof DUPLICATE_SIGNAL_KINDS)[number];
/** Cuentas que se guardan por señal (un móvil de tienda o una tarjeta de empresa no crece sin fin). */
export const MAX_UIDS_PER_SIGNAL = 50;

/** Normaliza el valor antes del hash: el mismo teléfono escrito con espacios es el mismo. */
export function normalizeSignalValue(kind: DuplicateSignalKind, raw: string): string | null {
  const v = raw.trim();
  if (!v) return null;
  if (kind === 'phone') {
    const plus = v.replace(/^00/, '+').startsWith('+');
    const digits = v.replace(/^00/, '').replace(/\D/g, '');
    return digits.length >= 8 ? `${plus ? '+' : ''}${digits}` : null;
  }
  if (kind === 'iban') return v.replace(/\s+/g, '').toUpperCase();
  return v;
}

export interface SignalUpdate {
  uids: string[];
  /** La cuenta es nueva en esta señal. */
  added: boolean;
  /** Otras cuentas que ya la tenían (si hay, se avisa). */
  others: string[];
}

export function planSignalUpdate(
  existing: readonly string[],
  uid: string,
  cap = MAX_UIDS_PER_SIGNAL,
): SignalUpdate {
  const uids = Array.from(new Set(existing.filter(Boolean)));
  if (uids.includes(uid)) return { uids, added: false, others: uids.filter((u) => u !== uid) };
  return { uids: [...uids, uid].slice(-cap), added: true, others: uids };
}

/** Clave del aviso de admin: una por señal compartida. */
export const duplicateAlertId = (kind: DuplicateSignalKind, hash: string): string =>
  `${kind}_${hash.slice(0, 32)}`;

export const DUPLICATE_ALERT_STATUSES = ['open', 'reviewed', 'dismissed'] as const;

// ---------- contratos de las callables (registry.ts) ----------

/** ai-semanticSearch: ids de anuncios activos por significado (los filtros finos se aplican en el cliente). */
export const SemanticSearchInputSchema = z.object({
  q: z.string().trim().min(2).max(120),
  category: z.string().max(80).nullable().optional(),
  country: CountryCodeSchema.nullable().optional(),
  limit: z.number().int().min(1).max(60).optional(),
});
export interface SemanticSearchOutput {
  hits: SemanticHit[];
  from: 'fresh' | 'cached' | 'disabled';
}

/** ai-similarItems: «También te puede interesar» de un anuncio. */
export const SimilarItemsInputSchema = z.object({ product_id: z.string().min(1).max(128) });
export interface SimilarItemsOutput {
  ids: string[];
}

/** ai-priceSuggestion: rango de precio para lo que se está publicando (aún sin guardar). */
export const PriceSuggestionInputSchema = z.object({
  title: z.string().trim().min(3).max(200),
  description: z.string().max(5000).optional(),
  category_id: z.string().min(1).max(80),
  brand: z.string().max(60).optional(),
  size: z.string().max(12).optional(),
  condition: z.string().max(20).optional(),
  currency: z.string().regex(/^[A-Z]{3}$/),
  /** Edición: no compararse consigo mismo. */
  product_id: z.string().max(128).optional(),
});
export interface PriceSuggestionOutput {
  suggestion: (PriceRange & { currency: string; basis: 'sold' | 'active' }) | null;
}

/** ai-suggestReplies: 2–3 respuestas rápidas para el vendedor al último mensaje del comprador. */
export const SuggestRepliesInputSchema = z.object({
  conversation_id: z.string().min(1).max(200),
  message_id: z.string().min(1).max(128),
  /** Idioma de la web del vendedor (respaldo si el mensaje es demasiado corto para saberlo). */
  language: LanguageCodeSchema,
});
export interface SuggestRepliesOutput {
  replies: string[];
}

/** ai-backfillEmbeddings (admin): un lote de anuncios sin vector o con el texto cambiado. */
export const BackfillEmbeddingsInputSchema = z.object({
  status: z.enum(EMBEDDED_STATUSES),
  cursor: z.string().max(128).nullable().optional(),
  limit: z.number().int().min(1).max(400).optional(),
  dry_run: z.boolean().optional(),
});
export interface BackfillEmbeddingsOutput {
  scanned: number;
  embedded: number;
  skipped: number;
  chars: number;
  next_cursor: string | null;
}

/** ai-scanDuplicateAccounts (admin): recorre los teléfonos de los perfiles y crea los avisos. */
export const ScanDuplicateAccountsInputSchema = z.object({ dry_run: z.boolean().optional() });
export interface ScanDuplicateAccountsOutput {
  users: number;
  shared_signals: number;
}

/** ai-reviewDuplicateAccount (admin): marcar un aviso como revisado o descartado. Nunca bloquea. */
export const ReviewDuplicateAccountInputSchema = z.object({
  alert_id: z.string().min(1).max(128),
  status: z.enum(DUPLICATE_ALERT_STATUSES),
  note: z.string().max(500).optional(),
});
